import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { NotificationTemplatesTab } from '@/components/notifications/NotificationTemplatesTab';

// Icons for notification sections
const UserSettingsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5a3.5 3.5 0 0 1 3.5 3.5a3.5 3.5 0 0 1-3.5 3.5m7.43-2.53c.04-.32.07-.64.07-.97c0-.33-.03-.66-.07-1l2.11-1.63c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.31-.61-.22l-2.49 1c-.52-.39-1.06-.73-1.69-.98l-.37-2.65A.506.506 0 0 0 14 2h-4c-.25 0-.46.18-.5.42l-.37 2.65c-.63.25-1.17.59-1.69.98l-2.49-1c-.22-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64L4.57 11c-.04.34-.07.67-.07 1s.03.65.07.97l-2.11 1.66c-.19.15-.25.42-.12.64l2 3.46c.12.22.39.3.61.22l2.49-1.01c.52.4 1.06.74 1.69.99l.37 2.65c.04.24.25.42.5.42h4c.25 0 .46-.18.5-.42l.37-2.65c.63-.26 1.17-.59 1.69-.99l2.49 1.01c.22.08.49 0 .61-.22l2-3.46c.12-.22.07-.49-.12-.64l-2.11-1.66Z"
    />
  </Icon>
);

const TemplatesIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"
    />
  </Icon>
);

const ChannelsIcon = () => (
  <Icon viewBox="0 0 24 24" boxSize={5}>
    <path
      fill="currentColor"
      d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5l-8-5V6l8 5l8-5v2z"
    />
  </Icon>
);

export const TemplatesPage: React.FC = () => {
  const { t } = useTranslation();

  // Theme colors
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Update sidebars with context actions and subsections
  useEffect(() => {
    // Update right sidebar with actions
    if (window.updateRightSidebar) {
      window.updateRightSidebar([
        {
          label: t('notifications.createTemplate'),
          colorScheme: 'green',
          onClick: () => {
            // Template creation logic
          },
        },
        {
          label: t('notifications.importTemplate'),
          colorScheme: 'blue',
          variant: 'outline',
          onClick: () => {
            // Template import logic
          },
        },
      ]);
    }

    // Update left sidebar with subsections info for notifications
    if (window.updateLeftSidebar) {
      const notificationSections = [
        {
          path: '/notifications/user-settings',
          label: t('notifications.userSettings'),
          icon: UserSettingsIcon,
        },
        {
          path: '/notifications/templates',
          label: t('notifications.templates'),
          icon: TemplatesIcon,
        },
        {
          path: '/notifications/channels',
          label: t('notifications.channels'),
          icon: ChannelsIcon,
        },
      ];

      window.updateLeftSidebar({
        section: 'notifications',
        subsections: notificationSections
      });
    }

    return () => {
      if (window.updateRightSidebar) {
        window.updateRightSidebar([]);
      }
      // Don't clear left sidebar subsections - let them persist for navigation
    };
  }, [t]);

  return (
    <Box minH="100vh" bg={bgColor}>
      <Container maxW="7xl" py={6}>
        <Heading size="lg" mb={6} color="text.primary">
          {t('notifications.templates')}
        </Heading>

        <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} p={6}>
          <NotificationTemplatesTab />
        </Box>
      </Container>
    </Box>
  );
};

 